import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sentinelApi } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  ArrowLeft, Clock, Shield, Send, Plus, Trash2, Edit2, Loader2,
  Calendar, AlertTriangle, CheckCircle, XCircle, Timer, Crosshair, Paperclip, X, File
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/layout/Header';

export default function SentinelMailPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sentinels, setSentinels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState('scheduled');
  const [form, setForm] = useState({
    to_emails: '',
    subject: '',
    body: '',
    send_at: '',
    trigger_days: 30,
  });
  const [attachments, setAttachments] = useState([]);
  const [creating, setCreating] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => { loadSentinels(); }, []);

  const loadSentinels = async () => {
    try {
      const res = await sentinelApi.list();
      setSentinels(res.sentinels);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFiles = (files) => {
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments(prev => [...prev, {
          name: file.name,
          size: `${(file.size / 1024).toFixed(1)}KB`,
          type: file.type,
          data: base64,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCreate = async () => {
    if (!form.to_emails.trim()) return toast.error('Enter at least one recipient');
    if (!form.subject.trim() && !form.body.trim()) return toast.error('Enter a subject or body');
    if (createType === 'scheduled' && !form.send_at) return toast.error('Pick a send date');
    if (createType === 'dead_switch' && form.trigger_days < 1) return toast.error('Trigger days must be at least 1');

    setCreating(true);
    try {
      const recipients = form.to_emails.split(',').map(e => e.trim()).filter(Boolean);
      await sentinelApi.create({
        sentinel_type: createType,
        to_emails: recipients,
        subject: form.subject,
        body: form.body,
        attachments: attachments,
        send_at: createType === 'scheduled' ? new Date(form.send_at).toISOString() : undefined,
        trigger_days: createType === 'dead_switch' ? form.trigger_days : undefined,
      });
      toast.success('Sentinel Mail created');
      setShowCreate(false);
      setForm({ to_emails: '', subject: '', body: '', send_at: '', trigger_days: 30 });
      setAttachments([]);
      loadSentinels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this Sentinel Mail?')) return;
    try {
      await sentinelApi.cancel(id);
      toast.success('Cancelled');
      loadSentinels();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  const statusBadge = (s) => {
    switch (s) {
      case 'pending': return <Badge className="bg-amber-500/20 text-amber-400 text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'sent': return <Badge className="bg-emerald-500/20 text-emerald-400 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'cancelled': return <Badge className="bg-gray-500/20 text-gray-400 text-xs"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case 'failed': return <Badge className="bg-red-500/20 text-red-400 text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
      default: return <Badge className="bg-gray-500/20 text-gray-400 text-xs">{s}</Badge>;
    }
  };

  const scheduled = sentinels.filter(s => s.sentinel_type === 'scheduled');
  const deadSwitches = sentinels.filter(s => s.sentinel_type === 'dead_switch');

  return (
    <div className="min-h-screen bg-[#0a0a18]">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#6D4AFF] to-cyan-500 rounded-xl flex items-center justify-center">
              <Crosshair className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Sentinel Mail</h1>
              <p className="text-sm text-gray-400">Schedule emails & set up dead man's switches</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]">
            <Plus className="w-4 h-4 mr-2" /> New Sentinel
          </Button>
        </div>

        <Tabs defaultValue="scheduled" className="space-y-6">
          <TabsList className="bg-[#1a1a2e] border border-[#2d2d44]">
            <TabsTrigger value="scheduled" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" /> Scheduled ({scheduled.length})
            </TabsTrigger>
            <TabsTrigger value="dead-switch" className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" /> Dead Switch ({deadSwitches.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="scheduled">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" /></div>
            ) : scheduled.length === 0 ? (
              <Card className="bg-[#1a1a2e] border-[#2d2d44]">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No scheduled emails</p>
                  <p className="text-sm text-gray-600 mt-1">Write now, send later — on your schedule</p>
                  <Button onClick={() => { setCreateType('scheduled'); setShowCreate(true); }} variant="outline" className="mt-4 border-[#6D4AFF]/40 text-[#6D4AFF]">
                    <Plus className="w-4 h-4 mr-2" /> Schedule an Email
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {scheduled.map(s => (
                  <Card key={s.id} className="bg-[#1a1a2e] border-[#2d2d44]">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {statusBadge(s.status)}
                            <span className="text-xs text-gray-500">To: {s.to_emails.join(', ')}</span>
                          </div>
                          <p className="text-white font-medium">{s.subject || '(No subject)'}</p>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">{s.body}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Send: {formatDate(s.send_at)}</span>
                            {s.sent_at && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Sent: {formatDate(s.sent_at)}</span>}
                          </div>
                        </div>
                        {s.status === 'pending' && (
                          <Button onClick={() => handleCancel(s.id)} variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="dead-switch">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" /></div>
            ) : deadSwitches.length === 0 ? (
              <Card className="bg-[#1a1a2e] border-[#2d2d44]">
                <CardContent className="py-12 text-center">
                  <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No dead man's switches</p>
                  <p className="text-sm text-gray-600 mt-1">Auto-send emails if you don't log in for a set number of days</p>
                  <Button onClick={() => { setCreateType('dead_switch'); setShowCreate(true); }} variant="outline" className="mt-4 border-[#6D4AFF]/40 text-[#6D4AFF]">
                    <Plus className="w-4 h-4 mr-2" /> Create Dead Switch
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {deadSwitches.map(s => (
                  <Card key={s.id} className="bg-[#1a1a2e] border-[#2d2d44]">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {statusBadge(s.status)}
                            <Badge className="bg-red-500/10 text-red-400 text-xs border border-red-500/20">
                              <Timer className="w-3 h-3 mr-1" /> {s.trigger_days} days no-login
                            </Badge>
                          </div>
                          <p className="text-white font-medium">{s.subject || '(No subject)'}</p>
                          <p className="text-sm text-gray-500 mt-1">To: {s.to_emails.join(', ')}</p>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-1">{s.body}</p>
                          {s.sent_at && <p className="text-xs text-emerald-500 mt-2">Triggered & sent: {formatDate(s.sent_at)}</p>}
                        </div>
                        {s.status === 'pending' && (
                          <Button onClick={() => handleCancel(s.id)} variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crosshair className="w-5 h-5 text-[#6D4AFF]" />
              New Sentinel Mail
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {createType === 'scheduled' ? 'Schedule an email to send later' : 'Auto-send if you don\'t log in'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type Toggle */}
            <div className="flex gap-2">
              <Button
                variant={createType === 'scheduled' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCreateType('scheduled')}
                className={createType === 'scheduled' ? 'bg-[#6D4AFF]' : 'border-[#2d2d44] text-gray-400'}
              >
                <Calendar className="w-4 h-4 mr-1" /> Schedule Send
              </Button>
              <Button
                variant={createType === 'dead_switch' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCreateType('dead_switch')}
                className={createType === 'dead_switch' ? 'bg-[#6D4AFF]' : 'border-[#2d2d44] text-gray-400'}
              >
                <Shield className="w-4 h-4 mr-1" /> Dead Switch
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">To (comma-separated)</Label>
              <Input
                value={form.to_emails}
                onChange={(e) => setForm({ ...form, to_emails: e.target.value })}
                placeholder="john, jane@mypatriotmail.org"
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
              <p className="text-xs text-gray-600">@mypatriotmail.org is added automatically</p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Email subject"
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Message</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Write your message..."
                className="bg-[#252540] border-[#2d2d44] text-white min-h-32"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300">Attachments</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#2d2d44] text-gray-400 text-xs"
                >
                  <Paperclip className="w-3 h-3 mr-1" /> Add File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => { if (e.target.files?.length) handleAddFiles(e.target.files); e.target.value = ''; }}
                  className="hidden"
                />
              </div>
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#252540] rounded-lg">
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4 text-[#6D4AFF]" />
                        <span className="text-xs text-white truncate max-w-[200px]">{att.name}</span>
                        <span className="text-xs text-gray-500">{att.size}</span>
                      </div>
                      <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {createType === 'scheduled' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Send Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={form.send_at}
                  onChange={(e) => setForm({ ...form, send_at: e.target.value })}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            )}

            {createType === 'dead_switch' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Trigger after how many days without login?</Label>
                <Input
                  type="number"
                  value={form.trigger_days}
                  onChange={(e) => setForm({ ...form, trigger_days: parseInt(e.target.value) || 1 })}
                  min={1}
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-xs text-amber-300">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    This email will automatically send if you don't log in for {form.trigger_days} days. Log in regularly to keep it from triggering.
                  </p>
                </div>
              </div>
            )}

            <Button onClick={handleCreate} disabled={creating} className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {createType === 'scheduled' ? 'Schedule Email' : 'Arm Dead Switch'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
